import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Request, Response, PagedResponse, createRequest } from '../domains/index';

// Re-export domain types for convenience
export type { Request, Response, PagedResponse };
export { createRequest };

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // Include cookies in requests
});

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    // Don't redirect on 401 - let the AuthContext handle it
    // This prevents infinite reload loops on the login page
    return Promise.reject(error);
  }
);

// Base API class with common CRUD operations
class BaseApi {
  protected instance: AxiosInstance;

  constructor() {
    this.instance = axiosInstance;
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    const response = await this.instance.get<Response<T>>(url, config);
    return response.data;
  }

  protected async post<T, D = unknown>(
    url: string,
    data?: Request<D> | D,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    // Wrap data in Request if it's not already wrapped
    const requestData = this.isRequest(data) ? data : createRequest(data as D);
    const response = await this.instance.post<Response<T>>(url, requestData, config);
    return response.data;
  }

  protected async put<T, D = unknown>(
    url: string,
    data?: Request<D> | D,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    // Wrap data in Request if it's not already wrapped
    const requestData = this.isRequest(data) ? data : createRequest(data as D);
    const response = await this.instance.put<Response<T>>(url, requestData, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    const response = await this.instance.delete<Response<T>>(url, config);
    return response.data;
  }

  protected async patch<T, D = unknown>(
    url: string,
    data?: Request<D> | D,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    // Wrap data in Request if it's not already wrapped
    const requestData = this.isRequest(data) ? data : createRequest(data as D);
    const response = await this.instance.patch<Response<T>>(url, requestData, config);
    return response.data;
  }

  // Type guard to check if data is already wrapped in Request
  private isRequest<D>(data: unknown): data is Request<D> {
    return (
      data !== null &&
      typeof data === 'object' &&
      'data' in data &&
      'isValid' in data
    );
  }
}

// Export the axios instance for direct use (e.g., in AuthContext)
export const api = axiosInstance;

export default BaseApi;
